import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { X } from '@phosphor-icons/react';
import { PhoneFrame } from '../PhoneFrame';
import { FormatToggle } from './FormatToggle';
import type { Format } from '../../types/snak';
interface OverlayProps {
  eyebrow: string;
  format: Format;
  onFormatChange: (format: Format) => void;
  onClose: () => void;
  /** In phone format, present the content inside the device. Defaults to true. */
  frame?: boolean;
  children: React.ReactNode;
}

/** Full-page reading surface for the explanation layers. */
export function Overlay({
  eyebrow,
  format,
  onFormatChange,
  onClose,
  frame = true,
  children
}: OverlayProps) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  const framed = frame && format === 'mobile';
  const content = <motion.div initial={{
    opacity: 0,
    y: 16
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5,
    delay: 0.08,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-159" data-mp="bflqw44712yq">
      {children}
    </motion.div>;
  return <motion.div role="dialog" aria-modal="true" aria-label={eyebrow} initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.3,
    ease: [0.22, 1, 0.36, 1]
  }} className="fixed inset-0 z-50 flex flex-col bg-[#F7F5F0] font-body" data-id="element-160" data-mp="2itihy8jmqap">
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[rgba(1,51,43,0.1)] px-5 py-3 lg:px-10" data-id="element-161" data-mp="lfkx3ud54h8f">
        <span className="truncate font-body text-[10px] uppercase tracking-[0.24em] text-[rgba(1,51,43,0.45)]" data-id="element-162" data-mp="1ivs32sjcb7af">
          {eyebrow}
        </span>
        <div className="flex shrink-0 items-center gap-2" data-id="element-163" data-mp="1e25ogutm8tbz">
          <FormatToggle value={format} onChange={onFormatChange} data-id="element-164" data-mp="1x1jyszpw5v98" />
          <button type="button" onClick={onClose} aria-label="Close" className="inline-flex h-9 items-center gap-2 rounded-full border border-[rgba(1,51,43,0.18)] px-4 font-body text-[10px] uppercase tracking-[0.16em] text-background transition-colors hover:border-[rgba(1,51,43,0.5)] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" data-id="element-165" data-mp="21z284r7tmchr">
            <X size={13} weight="bold" aria-hidden="true" data-id="element-166" data-mp="1j942ehrhwvha" />
            <span className="hidden sm:inline" data-id="element-167" data-mp="1j6hmnuugb9nz">Close</span>
          </button>
        </div>
      </div>

      {framed ? <div className="flex flex-1 items-center justify-center overflow-hidden px-4 py-5" data-id="element-168" data-mp="19aen8wqzhp4q">
          <PhoneFrame label={`${eyebrow} in phone format`} sizeClassName="h-[clamp(500px,calc(100vh-136px),860px)] w-auto aspect-[344/688]" data-id="element-169" data-mp="2zndh0k5xr5ux">
            {content}
          </PhoneFrame>
        </div> : <div className="flex-1 overflow-y-auto" data-id="element-170" data-mp="s2572xxqhqqx">{content}</div>}
    </motion.div>;
}